import { CONTENT } from "@/constants/contentTestIds";
import { FIELD_TYPES } from "@/components/DynamicFieldForm";

/**
 * Edits a list of FieldDefinitions in place. Used by both Content Categories
 * (the schema tab) and Custom Attributes (the schema editor).
 */
export default function FieldSchemaEditor({ fields, onChange, testidPrefix = CONTENT.catFieldRowPrefix }) {
  const update = (idx, patch) => {
    const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange(next);
  };
  const remove = (idx) => onChange(fields.filter((_, i) => i !== idx));
  const add = () =>
    onChange([
      ...fields,
      {
        field_id: `tmp_${Date.now()}`,
        key: "",
        name: "",
        type: "text",
        required: false,
        order: ((fields[fields.length - 1]?.order || 0) + 10),
      },
    ]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.18em] text-[#6B6358] px-2">
        <div className="col-span-3">Key</div>
        <div className="col-span-4">Display Name</div>
        <div className="col-span-3">Type</div>
        <div className="col-span-1 text-center">Req</div>
        <div className="col-span-1"></div>
      </div>
      {fields.map((f, idx) => (
        <div key={f.field_id || idx}>
          <div
            data-testid={`${testidPrefix}${idx}`}
            className="grid grid-cols-12 gap-3 items-center"
          >
            <input
              data-testid={`${CONTENT.catFieldKey}${idx}`}
              placeholder="snake_case_key"
              value={f.key}
              onChange={(e) => update(idx, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
              className="col-span-3 px-2 py-1.5 border border-[#1A1F2C]/15 rounded text-xs font-mono bg-[#F8F5EE]"
            />
            <input
              data-testid={`${CONTENT.catFieldName}${idx}`}
              placeholder="Display Name"
              value={f.name}
              onChange={(e) => update(idx, { name: e.target.value })}
              className="col-span-4 px-2 py-1.5 border border-[#1A1F2C]/15 rounded text-sm bg-[#F8F5EE]"
            />
            <select
              data-testid={`${CONTENT.catFieldType}${idx}`}
              value={f.type}
              onChange={(e) => update(idx, { type: e.target.value })}
              className="col-span-3 px-2 py-1.5 border border-[#1A1F2C]/15 rounded text-xs bg-[#F8F5EE]"
            >
              {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              data-testid={`${CONTENT.catFieldRequired}${idx}`}
              type="checkbox"
              checked={!!f.required}
              onChange={(e) => update(idx, { required: e.target.checked })}
              className="col-span-1 justify-self-center"
            />
            <button
              data-testid={`${CONTENT.catFieldRemove}${idx}`}
              type="button"
              onClick={() => remove(idx)}
              className="col-span-1 text-[#B5462F] text-xs"
            >
              Remove
            </button>
          </div>
          {f.type === "binding_slug" && (
            <div className="grid grid-cols-12 gap-3 mt-1.5">
              <div className="col-span-7"></div>
              <div className="col-span-4 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.16em] text-[#6B6358]">Asset class</span>
                <select
                  data-testid={`field-binding-class-${idx}`}
                  value={f.binding_asset_class || "logo"}
                  onChange={(e) => update(idx, { binding_asset_class: e.target.value })}
                  className="flex-1 px-2 py-1.5 border border-[#1A1F2C]/15 rounded text-xs bg-[#F8F5EE]"
                >
                  <option value="logo">Logo</option>
                  <option value="color">Color</option>
                  <option value="font">Font</option>
                  <option value="shape">Shape</option>
                </select>
              </div>
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        data-testid={CONTENT.catFieldAddButton}
        onClick={add}
        className="px-4 py-1.5 text-sm border border-[#1A1F2C]/20 rounded-full hover:border-[#1A1F2C]/40"
      >
        + Add field
      </button>
    </div>
  );
}
